import { ReactElement } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { updateModalStatus } from 'redux/modals/actions'
import { RootState } from 'redux/store'
import { useLocation } from 'react-router-dom'

import featureFlags from 'const/featureFlags'
import getMFEConfig from 'helpers/getMFEConfig'
import { makeAPaymentModalContainerId } from 'const/modalContainerId'
import { useAccountSummary, useCreditAccounts } from 'const/syfApiQueries'
import findAccount from 'helpers/findAccount'
import getHelpNumber from 'helpers/getHelpNumbers'
import getLanguageFromStorage from 'helpers/getLanguageFromStorage'
import triggerAnalyticsEvent from 'helpers/triggerAnalyticsEvent'
import triggerQualtrics from 'helpers/triggerQualtrics'
import PaymentModal from 'make-a-payment'
import routes from 'const/routes'

const pathToPageInfo = {
  [routes.ACCOUNT_SERVICES]: {
    PageFunction: 'account services',
    PageKind: 'account services'
  },
  [routes.ACTIVITY]: {
    PageFunction: 'activity',
    PageKind: 'activity'
  },
  // needs to be updated dynamically - currently updating with default info
  [routes.PROMOTIONS]: {
    PageFunction: 'promo_tab',
    PageKind: 'activity'
  }
}

const MakePaymentModal = (): ReactElement => {
  const { modalId, selectedAccount } = useSelector(
    (state: RootState) => ({
      modalId: state.modals.modalId,
      // TODO: pass in as prop when MAP accepts new modalTriggerId
      modalTriggerId: state.modals.modalTriggerId,
      selectedAccount: state.modals.selectedAccount
    }),
    shallowEqual
  )

  const { data: { accounts = [] } = {} } = useCreditAccounts()
  const { data: accountSummary } = useAccountSummary(selectedAccount)

  const location = useLocation()
  const dispatch = useDispatch()
  const account = findAccount(accounts, selectedAccount)
  const accountLast4 = account?.info?.accountLast4
  const clientId = accountSummary?.product?.clientId
  const { type: productType } = account?.product || {}
  const helpNumber = getHelpNumber(clientId, productType)
  const { hosts } = getMFEConfig('mfe')
  const mapFeatureFlags = {
    isDigitalCollectionsEnabled: featureFlags.isDigitalCollectionsEnabled,
    isLateFeeDateEnabled: featureFlags.isLateFeeDateEnabled,
    isManagePaymentMethodsEnabled: featureFlags.isManagePaymentMethodsEnabled,
    isSavingCheckingEnabled: featureFlags.isSavingCheckingEnabled
  }

  const lang = getLanguageFromStorage()

  const handleClose = () => {
    // update pageInfo based on the page where MAP is opened/closed
    const currentPathname = location.pathname
    const pageInfo = pathToPageInfo[currentPathname] ?? {
      PageFunction: 'dashboard',
      PageKind: 'multiaccount'
    }
    triggerAnalyticsEvent(pageInfo)
    dispatch(updateModalStatus(''))
  }

  return (
    <PaymentModal
      id="make-a-payment"
      accountLast4={accountLast4}
      containerId={makeAPaymentModalContainerId(selectedAccount)}
      isOpen={modalId === 'MAP'}
      handleClose={handleClose}
      accountId={selectedAccount}
      triggerAnalyticsEvent={triggerAnalyticsEvent}
      lang={lang}
      apiErrorCallback={(error) => error}
      featureFlags={mapFeatureFlags}
      hosts={hosts}
      helpNumber={helpNumber}
      handleCloseOnConversion={() => {
        triggerQualtrics()
      }}
    />
  )
}

export default MakePaymentModal
